// =========================================================
// 📁 WithdrawEurosConfirm.js
// Page de confirmation AVANT conversion coins → euros
// Objectif :
//   1) Afficher le solde coins + estimation en euros
//   2) Afficher conditions / checkbox
//   3) Au clic : appeler le backend pour convertir
//   4) SI OK -> rediriger vers la page où l'user choisit IBAN / PayPal
// =========================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// =========================================================
// ✅ API AXIOS CENTRALISÉE
// - Important : on garde ton API "../axiosInstance"
// - Il doit pointer vers /api (via proxy/nginx) comme le reste
// =========================================================
import API from "../axiosInstance";

// =========================================================
// ✅ CSS déjà utilisé sur Retrait
// =========================================================
import "../styles/Retrait.css";

export default function WithdrawEurosConfirm() {
  // =======================================================
  // 🔀 navigate : sert UNIQUEMENT à changer de page
  // - retour arrière
  // - aller vers la page "IBAN / PayPal"
  // =======================================================
  const navigate = useNavigate();

  // =======================================================
  // 📌 STATES PRINCIPAUX
  // =======================================================
  const [coins, setCoins] = useState(0);             // Solde coins actuel
  const [estimatedEuros, setEstimatedEuros] = useState("0.00"); // Estimation affichée
  const [accepted, setAccepted] = useState(false);   // Checkbox conditions
  const [loading, setLoading] = useState(true);      // Ecran de chargement
  const [error, setError] = useState("");            // Erreur user-friendly

  // =======================================================
  // ✅ AU CHARGEMENT : récupérer solde + taux depuis backend
  // Endpoint déjà OK chez toi :
  //   GET /api/coins/conversion/status
  // Réponse : { coins, euros, rates: { coin_to_euro, euro_to_coin } }
  // =======================================================
  useEffect(() => {
    async function loadStatus() {
      try {
        const res = await API.get("/coins/conversion/status");

        // ---------------------------
        // Sécurisation
        // ---------------------------
        const coinsValue = Number(res.data?.coins || 0);
        const rate = Number(res.data?.rates?.coin_to_euro || 0);

        setCoins(coinsValue);

        // ---------------------------
        // Calcul estimation :
        // coin_to_euro = "combien de coins pour 1€"
        // Exemple : 100 coins = 1€
        // Donc estimation = coins / 100
        // ---------------------------
        if (rate > 0) {
          setEstimatedEuros((coinsValue / rate).toFixed(2));
        } else {
          setEstimatedEuros("0.00");
        }
      } catch (err) {
        console.error("❌ Erreur récupération solde coins/euros :", err);
        setError("Impossible de récupérer vos informations.");
      } finally {
        setLoading(false);
      }
    }

    loadStatus();
  }, []);

  // =======================================================
  // ✅ ACTION PRINCIPALE : confirmer la conversion
  // BUT :
  //   1) Vérifier checkbox + minimum
  //   2) POST conversion backend
  //   3) Si OK -> aller sur page IBAN/PAYPAL (étape suivante)
  // =======================================================
  async function handleConfirm() {
    // -------------------------------------------------------
    // 🔒 Sécurité : si pas coché -> pas d’action
    // -------------------------------------------------------
    if (!accepted) return;

    // -------------------------------------------------------
    // 🔒 Exemple règle : minimum 100 coins (optionnel)
    // (si tu veux imposer un minimum)
    // -------------------------------------------------------
    if (coins < 100) {
      setError("Minimum conversion : 100 coins.");
      return;
    }

    try {
      // -----------------------------------------------------
      // 1) Conversion coins -> euros (backend)
      // IMPORTANT : si ce POST renvoie erreur => catch
      // -----------------------------------------------------
      await API.post("/coins/conversion/coins-to-euros");

      // -----------------------------------------------------
      // 2) SI conversion OK :
      // 👉 on envoie l’utilisateur vers la page "Retrait euros"
      // où il choisit IBAN / PayPal + formulaire
      // -----------------------------------------------------
      // ✅ Mets ici LA ROUTE de ta future page formulaire
      // Exemple conseillé :
      //   /retrait/euros  (page "solde euros + choix méthode")
      // -----------------------------------------------------
      navigate("/retrait/euros");
    } catch (err) {
      console.error("❌ Erreur conversion coins->euros :", err);
      setError("Erreur lors de la conversion. Veuillez réessayer.");
    }
  }

  // =======================================================
  // ⏳ CHARGEMENT
  // =======================================================
  if (loading) {
    return (
      <div className="retrait-page">
        <div className="retrait-shell">
          <p className="retrait-subtitle">Chargement…</p>
        </div>
      </div>
    );
  }

  // =======================================================
  // ❌ ERREUR
  // IMPORTANT :
  // - On affiche l’erreur en haut
  // - On laisse un bouton retour
  // =======================================================
  if (error) {
    return (
      <div className="retrait-page">
        <div className="retrait-shell">
          <p className="retrait-subtitle">{error}</p>
          <button className="retrait-back" onClick={() => navigate(-1)}>
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  // =======================================================
  // ✅ RENDER PRINCIPAL
  // =======================================================
  return (
    <div className="retrait-page">
      <div className="retrait-shell">

        {/* =================================================
            HEADER
        ================================================= */}
        <div className="retrait-header">
          <div>
            <h2 className="retrait-title">💶 Retrait en euros</h2>
            <p className="retrait-subtitle">
              Conversion de vos coins en argent réel.
            </p>
          </div>

          <button className="retrait-back" onClick={() => navigate(-1)}>
            ← Retour
          </button>
        </div>

        {/* =================================================
            RÉSUMÉ : coins + estimation €
        ================================================= */}
        <div className="retrait-panel">
          <div className="retrait-grid">

            <div className="retrait-card">
              <h3 className="retrait-card-title">🪙 Coins à convertir</h3>
              <p className="retrait-card-desc">{coins} coins</p>
              <p className="retrait-card-desc" style={{ opacity: 0.7 }}>
                Minimum conversion : 100 coins
              </p>
            </div>

            <div className="retrait-card euros">
              <h3 className="retrait-card-title">💶 Montant estimé</h3>
              <p className="retrait-card-desc">{estimatedEuros} €</p>
              <p className="retrait-card-desc" style={{ opacity: 0.7 }}>
                Basé sur le taux EVO LIVE actuel.
              </p>
            </div>

          </div>
        </div>

        {/* =================================================
            CONDITIONS / TEXTE LÉGAL
            (On reste simple, clair, efficace)
        ================================================= */}
        <div className="retrait-panel" style={{ marginTop: 18 }}>
          <p className="retrait-card-desc">
            En confirmant cette opération, vous acceptez que :
          </p>

          <ul className="retrait-card-desc" style={{ marginTop: 10 }}>
            <li>• La conversion coins → euros est définitive et irréversible une fois validée.</li>
            <li>• Les euros crédités seront disponibles pour retrait (PayPal / IBAN) ou reconversion selon les règles EVO LIVE.</li>
            <li>• Vous devez fournir des informations exactes (identité, coordonnées, méthode de paiement). Toute erreur peut entraîner un retard ou un refus.</li>
            <li>• EVO LIVE peut appliquer des mesures de sécurité (vérification, seuils, anti-fraude) et bloquer une opération en cas d’activité suspecte.</li>
          </ul>

          {/* ✅ Checkbox obligatoire */}
          <label style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span className="retrait-card-desc">
              J’accepte les conditions de conversion et de retrait.
            </span>
          </label>
        </div>

        {/* =================================================
            ACTION PRINCIPALE
            👉 le bouton appelle handleConfirm()
            👉 handleConfirm fait la conversion + redirection vers /retrait/euros
        ================================================= */}
        <div style={{ marginTop: 22 }}>
          <button
            className="retrait-back"
            disabled={!accepted}
            onClick={handleConfirm}
            style={{
              opacity: accepted ? 1 : 0.5,
              cursor: accepted ? "pointer" : "not-allowed",
            }}
          >
            Confirmer la conversion
          </button>
        </div>

      </div>
    </div>
  );
}
